import { colors } from "@indeed/ifl-tokens/react-native";
import { useEffect, useState } from "react";
import { StyleSheet } from "react-native";

import { Box, ArrowLeft, Button, BottomSheetHeader } from "../../../../../../mads";
import { Close } from "../../../../../../mads/icons";
import { useI18n } from "../../../../../../shared/I18n";
import { useIsBottomSheet, useIsFullScreen } from "../../../../../../shared/util/ScreenType";
import { useViewjobOneGraphModel } from "../../../../context/ViewJobDataContext";
import { useIsDesktop } from "../../../../context/ViewJobScreenDisplay";
import { useCloseVJ } from "../../../../lib/useCloseVJ";
import getJobKeyFromUrl from "../../../../util/getJobKeyFromUrl";
import { ShareButton } from "../ShareButton/ShareButton";
import type { NavigationBarProps } from "./types";

const BOTTOM_SHEET_VJ_Z_INDEX_BASE = 9000;

const styles = StyleSheet.create({
  root: {
    height: 56,
    flexDirection: "row",
    flexBasis: "auto",
    flexShrink: 0,
    flexGrow: 0,
    alignItems: "center",
    top: 0,
    width: "100%",
    zIndex: 100,
    justifyContent: "space-between",
  },
  rightSide: {
    flexGrow: 1,
    flexShrink: 0,
    flexBasis: 0,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    marginTop: 15,
    marginRight: 10,
  },
  leftSide: {
    flexGrow: 1,
    flexShrink: 0,
    flexBasis: 0,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    marginLeft: 10,
  },
  center: {
    // Make it take up more space so that keyboard navigation can find it more easily.
    // This should actually be handled by the keyboard navigation system,
    // but react native doesn't have good support.
    flexGrow: 2.5,
    flexShrink: 0,
    flexBasis: 0,
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  },
  button: {
    height: "100%",
    justifyContent: "center",
  },
  centerButton: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    zIndex: 1,
  },
  bar: {
    width: 35,
    borderRadius: 6,
    height: 4,
    backgroundColor: "rgba(0, 0, 0, .5)",
  },
  withBorder: {
    borderBottomWidth: 1,
    borderBottomColor: colors.borderWeak,
  },
  heading: {
    width: "100%",
    textAlign: "center",
  },
});

// Styles for the BOTTOM_SHEET navigation bar (embedded in homepage).
// Matches the layout of BottomSheetModalWrapper's ModalHeader: share left, close right.
// The NavigationBar height, exported so a spacer can be rendered to offset content below the fixed bar.
export const BOTTOM_SHEET_NAV_HEIGHT = 44;
const BOTTOM_SHEET_TOP_OFFSET = 32;

const bottomSheetStyles = StyleSheet.create({
  fixedContainer: {
    // @ts-expect-error - position 'fixed' is valid on web (react-native-web)
    position: "fixed",
    top: BOTTOM_SHEET_TOP_OFFSET,
    left: 0,
    right: 0,
    zIndex: BOTTOM_SHEET_VJ_Z_INDEX_BASE + 1,
    backgroundColor: "white",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    overflow: "hidden",
  },
  spacer: {
    // BottomSheetHeader has ~10px padding top + PageHeader min height (44px)
    height: BOTTOM_SHEET_NAV_HEIGHT + 10,
    flexShrink: 0,
  },
  leftSlot: {
    width: 44,
    height: 44,
    alignItems: "center",
    justifyContent: "center",
  },
});

export const SCROLL_POSITION_ANCHOR = "#vj-back-on-indeed";

export const getBackHref = (isMobile: boolean): string => {
  const redirectPath = isMobile ? "/m" : "/";
  const { host } = window.location;
  const referrer = document.referrer;

  if (referrer && !referrer.includes(host)) {
    return `${redirectPath}?from=conditionalButton`;
  }

  const url = new URL(window.location.href);
  const backHrefUrlParam = url.searchParams.get("backhref");
  const decodedBackHref = backHrefUrlParam && decodeURI(backHrefUrlParam);
  // allow only a relative path
  if (decodedBackHref && decodedBackHref.startsWith("/")) {
    return backHrefUrlParam;
  }

  return SCROLL_POSITION_ANCHOR;
};

function useClientValues(isMobile: boolean): { backHref: string | undefined; canShare: boolean } {
  const [backHref, setBackHref] = useState<string>();
  const [canShare, setCanShare] = useState(false);
  useEffect(() => {
    setBackHref(getBackHref(isMobile));
    setCanShare(window.navigator?.canShare?.() ?? false);
  }, [isMobile]);
  return { backHref, canShare };
}

interface BottomSheetNavigationBarProps {
  showShareButton?: boolean;
}

function BottomSheetNavigationBar({ showShareButton }: BottomSheetNavigationBarProps) {
  const i18n = useI18n();
  const closeVJ = useCloseVJ();

  return (
    <>
      <Box sx={bottomSheetStyles.fixedContainer}>
        <BottomSheetHeader
          title=""
          headerLeft={
            <Box sx={bottomSheetStyles.leftSlot}>
              <ShareButton disabled={!showShareButton} renderWhenUnavailable />
            </Box>
          }
          headerRight={
            <Button
              onPress={closeVJ}
              aria-label={i18n.gettext("Close")}
              variant="tertiary"
              iconOnly={true}
              size="md"
              iconAfter={<Close fill="black" />}
            />
          }
          showGrabber={false}
          showBorder={false}
        />
      </Box>
      {/* Spacer to push content below the fixed NavigationBar */}
      <Box sx={bottomSheetStyles.spacer} />
    </>
  );
}

interface FullScreenNavigationBarProps {
  showShareButton?: boolean;
  showBorder?: boolean;
  jobKey: string | null;
}

function FullScreenNavigationBar({
  showShareButton,
  showBorder = false,
  jobKey,
}: FullScreenNavigationBarProps) {
  const i18n = useI18n();
  const showBar = useIsFullScreen();
  const rootStyles = StyleSheet.flatten([styles.root, showBorder && styles.withBorder]);
  const { isMobile = false, shouldShowConditionalBackButton = false } =
    useViewjobOneGraphModel() ?? {};
  const { backHref, canShare } = useClientValues(isMobile);
  const isSoftNav = backHref === SCROLL_POSITION_ANCHOR;

  const clickHandler = () => {
    if (isSoftNav) {
      // TODO log `conditionalBackButtonClick` with Signals (mobtk, isFromOnIndeed: true)
      history.back(); // preserve scroll positions
    } else {
      // TODO log `conditionalBackButtonClick` with Signals (mobtk, isFromOnIndeed: false)
    }
  };

  if (!showBar || !shouldShowConditionalBackButton) {
    return null;
  }

  return (
    <Box sx={rootStyles}>
      <Box sx={styles.leftSide}>
        {backHref != null ? (
          <Button
            onPress={clickHandler}
            aria-label={i18n.pgettext("Conditional back button label", "Back")}
            variant="tertiary"
            href={!isSoftNav ? backHref : undefined}
          >
            <ArrowLeft fill="black" size="md" />
          </Button>
        ) : null}
      </Box>
      <Box sx={styles.center} />
      <Box sx={styles.rightSide}>
        {canShare && jobKey != null && showShareButton && <ShareButton />}
      </Box>
    </Box>
  );
}

export const NavigationBar: React.FC<NavigationBarProps> = ({
  url,
  showShareButton,
  showBorder = false,
}) => {
  const isBottomSheet = useIsBottomSheet();
  const isDesktop = useIsDesktop();
  const jobKey = getJobKeyFromUrl(url);

  if (isDesktop) {
    return null;
  }

  return isBottomSheet ? (
    <BottomSheetNavigationBar showShareButton={showShareButton} />
  ) : (
    <FullScreenNavigationBar
      showShareButton={showShareButton}
      showBorder={showBorder}
      jobKey={jobKey}
    />
  );
};
